import { supabase } from "@/integrations/supabase/client";
import { watermarkImage } from "@/lib/watermark";
import { toWebP, safeContentType, UPLOAD_MAX_INPUT_BYTES, isStaticImage } from "@/lib/image-convert";
import { sendTransactionalEmail } from "@/lib/email/send";
import {
  SETTING_WATERMARK_URL,
  SETTING_WATERMARK_X,
  SETTING_WATERMARK_Y,
  SETTING_WATERMARK_SCALE,
  SETTING_WATERMARK_OPACITY,
  getSettingText,
  getSettingInt,
} from "@/lib/app-settings";

export type CommunitySubmissionStatus = "pending" | "approved" | "rejected";

export type CommunitySubmission = {
  id: string;
  image_url: string;
  caption: string | null;
  theme: string | null;
  region: string | null;
  submitter_name: string;
  submitter_email: string;
  instagram_handle: string | null;
  website_url: string | null;
  bio: string | null;
  status: CommunitySubmissionStatus;
  admin_note: string | null;
  created_at: string;
  reviewed_at: string | null;
};

export type NewCommunitySubmission = {
  file: File;
  caption?: string;
  theme?: string;
  region?: string;
  submitter_name: string;
  submitter_email: string;
  instagram_handle?: string;
  website_url?: string;
  bio?: string;
  user_id?: string | null;
  instagram_consent?: boolean;
};

const MAX_FILE_BYTES = UPLOAD_MAX_INPUT_BYTES; // 25 MB

function sanitizeFileName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9.]+/g, "-").replace(/-+/g, "-").slice(0, 80);
}

export async function submitCommunityPhoto(input: NewCommunitySubmission): Promise<CommunitySubmission> {
  if (!isStaticImage(input.file)) {
    throw new Error("Nur statische Bilddateien erlaubt (keine GIFs).");
  }
  if (input.file.size > MAX_FILE_BYTES) {
    throw new Error("Bild ist zu groß (max. 25 MB).");
  }

  // OM-Selected-Wasserzeichen einbrennen (Fallback: Original, falls Canvas fehlschlägt)
  let uploadFile = input.file;
  try {
    const [imageUrl, x, y, scale, opacity] = await Promise.all([
      getSettingText(SETTING_WATERMARK_URL, null),
      getSettingInt(SETTING_WATERMARK_X, 50),
      getSettingInt(SETTING_WATERMARK_Y, 92),
      getSettingInt(SETTING_WATERMARK_SCALE, 25),
      getSettingInt(SETTING_WATERMARK_OPACITY, 94),
    ]);
    uploadFile = await watermarkImage(input.file, { imageUrl, x, y, scale, opacity });
  } catch (e) {
    console.warn("[watermark] fallback to original", e);
  }

  try {
    uploadFile = await toWebP(uploadFile);
  } catch {
    /* keep watermarked file as-is */
  }

  // The storage policy only accepts object names ending in jpg/jpeg/png/webp.
  // Derive the extension from the actual content type (not the original file
  // name) so e.g. an iPhone HEIC that could not be converted is rejected with a
  // clear message instead of a cryptic "row violates RLS" upload error.
  const contentType = safeContentType(uploadFile);
  const safeExt =
    contentType === "image/png" ? "png"
    : contentType === "image/webp" ? "webp"
    : contentType === "image/jpeg" ? "jpg"
    : null;
  if (!safeExt) {
    throw new Error(
      "Dieses Bildformat wird nicht unterstützt. Bitte als JPG, PNG oder WebP hochladen.",
    );
  }
  const baseName = sanitizeFileName(uploadFile.name).replace(/\.[a-z0-9]+$/i, "");
  const fileName = `${`${Date.now()}-${crypto.randomUUID()}-${baseName}`.slice(0, 170)}.${safeExt}`;
  const path = `submissions/${fileName}`;

  const { error: uploadErr } = await supabase.storage
    .from("community-selected")
    .upload(path, uploadFile, { cacheControl: "31536000", contentType, upsert: false });
  if (uploadErr) throw new Error(uploadErr.message);

  const { data: pub } = supabase.storage.from("community-selected").getPublicUrl(path);
  const image_url = pub.publicUrl;

  // Anon submitters cannot read rows back (no SELECT policy / grant), so we
  // intentionally do NOT request a representation here — `.select()` would
  // trip RLS and fail. The caller does not use the returned row.
  const { error } = await supabase.from("community_submissions").insert({
    image_url,
    caption: input.caption?.trim() || null,
    theme: input.theme?.trim() || null,
    region: input.region?.trim() || null,
    submitter_name: input.submitter_name.trim(),
    submitter_email: input.submitter_email.trim(),
    instagram_handle: input.instagram_handle?.trim() || null,
    website_url: input.website_url?.trim() || null,
    bio: input.bio?.trim() || null,
    user_id: input.user_id ?? null,
    instagram_consent: input.instagram_consent ?? false,
    instagram_consent_at: input.instagram_consent ? new Date().toISOString() : null,
  });
  if (error) throw new Error(error.message);

  // Interne Admin-Benachrichtigung (best-effort, blockiert nicht).
  try {
    await fetch("/api/public/admin-notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "selected",
        name: input.submitter_name.trim(),
        theme: input.theme?.trim() || undefined,
        region: input.region?.trim() || undefined,
      }),
    });
  } catch {
    /* Benachrichtigungsfehler darf die Einreichung nicht stören. */
  }

  return {
    id: "",
    image_url,
    caption: input.caption?.trim() || null,
    theme: input.theme?.trim() || null,
    region: input.region?.trim() || null,
    submitter_name: input.submitter_name.trim(),
    submitter_email: input.submitter_email.trim(),
    instagram_handle: input.instagram_handle?.trim() || null,
    website_url: input.website_url?.trim() || null,
    bio: input.bio?.trim() || null,
    status: "pending",
    admin_note: null,
    created_at: new Date().toISOString(),
    reviewed_at: null,
  } satisfies CommunitySubmission;
}

export async function fetchApprovedCommunitySubmissions(limit = 24): Promise<CommunitySubmission[]> {
  const { data, error } = await supabase
    .from("community_submissions_public" as never)
    .select("id,image_url,caption,theme,region,submitter_name,instagram_handle,website_url,bio,status,created_at,reviewed_at")
    .order("reviewed_at", { ascending: false })
    .limit(limit) as { data: Array<Omit<CommunitySubmission, "submitter_email" | "admin_note">> | null; error: unknown };
  if (error) {
    console.error("[community_submissions]", error);
    return [];
  }
  return (data ?? []).map((r) => ({ ...r, submitter_email: "", admin_note: null })) as CommunitySubmission[];
}

export async function listAllCommunitySubmissions(status: CommunitySubmissionStatus | "all"): Promise<CommunitySubmission[]> {
  const { data, error } = await supabase.rpc("admin_list_community_submissions", { _status: status });
  if (error) throw new Error(error.message);
  return (data ?? []) as CommunitySubmission[];
}

export async function updateCommunitySubmission(id: string, status: CommunitySubmissionStatus, admin_note: string | null): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  // Vorherigen Status laden, damit wir nur bei Übergang -> approved eine Mail senden
  const { data: prev } = await supabase
    .from("community_submissions")
    .select("status, submitter_email, submitter_name, image_url")
    .eq("id", id)
    .maybeSingle();
  const { error } = await supabase
    .from("community_submissions")
    .update({
      status,
      admin_note,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user?.id ?? null,
    })
    .eq("id", id);
  if (error) throw new Error(error.message);

  if (status === "approved" && prev && prev.status !== "approved" && prev.submitter_email) {
    try {
      await sendTransactionalEmail({
        templateName: "selected-approved",
        recipientEmail: prev.submitter_email,
        idempotencyKey: `selected-approved-${id}`,
        templateData: {
          name: prev.submitter_name ?? undefined,
          imageUrl: prev.image_url ?? null,
        },
      });
    } catch (e) {
      console.error("[selected-approved email]", e);
    }
  }

  if (status === "rejected" && prev && prev.status !== "rejected" && prev.submitter_email) {
    try {
      await sendTransactionalEmail({
        templateName: "rejection-notice",
        recipientEmail: prev.submitter_email,
        idempotencyKey: `selected-rejected-${id}`,
        templateData: {
          name: prev.submitter_name ?? undefined,
          message: admin_note ?? null,
        },
      });
    } catch (e) {
      console.error("[selected-rejected email]", e);
    }
  }
}

export async function deleteCommunitySubmission(id: string): Promise<void> {
  const { error } = await supabase.from("community_submissions").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

export type CreatorSelectedImage = {
  id: string;
  image_url: string;
  caption: string | null;
  theme: string | null;
  region: string | null;
  source: "community" | "curated";
};

/**
 * Liefert die Selected-Bilder eines Creators für die öffentliche Profilseite:
 * eigene freigegebene Community-Einreichungen (verknüpft über user_id) sowie
 * kuratierte Week-Picks (nur Fotografen, über die DB-id). Es werden keine
 * persönlichen Daten preisgegeben.
 */
export async function fetchSelectedForCreator(args: {
  userId?: string | null;
  photographerDbId?: string | null;
}): Promise<CreatorSelectedImage[]> {
  const out: CreatorSelectedImage[] = [];

  if (args.userId) {
    const { data, error } = await supabase.rpc("get_selected_for_creator", { _user_id: args.userId });
    if (error) {
      console.error("[get_selected_for_creator]", error);
    } else {
      for (const r of (data ?? []) as Array<{ id: string; image_url: string; caption: string | null; theme: string | null; region: string | null }>) {
        out.push({ id: r.id, image_url: r.image_url, caption: r.caption, theme: r.theme, region: r.region, source: "community" });
      }
    }
  }

  if (args.photographerDbId) {
    const { data, error } = await supabase
      .from("selected_week_picks")
      .select("id, image_url, caption")
      .eq("photographer_id", args.photographerDbId)
      .order("created_at", { ascending: false });
    if (error) {
      console.error("[selected_week_picks creator]", error);
    } else {
      for (const r of data ?? []) {
        out.push({ id: r.id, image_url: r.image_url, caption: r.caption ?? null, theme: null, region: null, source: "curated" });
      }
    }
  }

  return out;
}